import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Grid,
    TextField,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FacilityLevelOfCare, Payor, Plan } from '@finpay-estimation/estimation-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import { RootState } from 'src/shared/state/root-reducer';
import { NetworkType, MemberType } from '../types';

// Styled component for numeric fields with right-aligned text
const VOBNumericField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(0),
    '& input': {
        textAlign: 'right',
    },
    '& .MuiInputBase-root': {
        height: '40px', // Match the height of VOBFormFields inputs
    },
    '& .MuiOutlinedInput-input': {
        padding: '10px 14px', // Adjust padding to match VOBFormFields
    }
}));

// Add styled component for the checkbox label to prevent wrapping
const NoWrapFormControlLabel = styled(FormControlLabel)({
    whiteSpace: 'nowrap',
    margin: 0,
    '& .MuiFormControlLabel-label': {
        fontSize: '1.2rem',
    }
});

// Styled component for the framed section
const FramedSection = styled(Box)(({ theme }) => ({
    border: '2px solid #e0e0e0', // Match the 2px border of VOBFormFields
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    position: 'relative',
}));

// Styled component for frame label
const FrameLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'hasButton',
})<{ hasButton?: boolean }>(({ theme, hasButton }) => ({
    position: 'absolute',
    top: '-0.75rem',
    left: hasButton ? '36px' : '10px', // Move label right if there's a button
    backgroundColor: '#fff',
    padding: '0 5px',
    fontSize: '1.2rem',
    fontWeight: '400',
    color: 'rgba(0, 0, 0, 0.6)',
    lineHeight: '1.5rem',
}));

// Styled component for frame label button
const FrameLabelButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '-0.75rem',
    left: '10px',
    transform: 'translateY(-1px)',
    width: '20px',
    height: '20px',
    minWidth: '20px',
    padding: 0,
    backgroundColor: '#fff',
    zIndex: 1,
}));

const FieldLabelButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '-8px',
    left: '12px',
    width: '16px',
    height: '16px',
    minWidth: '16px',
    padding: 0,
    backgroundColor: '#fff',
    zIndex: 1,
}));

const LabelButtonWrapper = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
}));



// Styled component for override button
const OverrideButton = styled(Button)(({ theme }) => ({
    height: '36px',
    marginLeft: theme.spacing(2),
    textTransform: 'none',
}));

// Styled component for default benefits text
const DefaultBenefitsText = styled(Typography)(({ theme }) => ({
    fontStyle: 'italic',
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(2),
}));

// Styled component for classification name
const ClassificationNameText = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(0.5),
    display: 'block',
}));

// No Symbol SVG component
const NoSymbol = ({ isPressed }: { isPressed: boolean }) => {
    const color = isPressed ? '#ff0000' : '#999999';

    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="7" stroke={color} strokeWidth="1.5" fill="none" />
            <line x1="3.5" y1="3.5" x2="12.5" y2="12.5" stroke={color} strokeWidth="1.5" />
        </svg>
    );
};

// Override Symbol SVG component
const OverrideSymbol = ({ isPressed }: { isPressed: boolean }) => {
    const color = isPressed ? '#0066cc' : '#999999';

    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="7" stroke={color} strokeWidth="1.5" fill="none" />
            <path d="M4 8L7 11L12 5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

interface FacilityPayerOption {
    cfgPayorId: number;
    facilityPayorName?: string;
    inNetwork: boolean;
    facilityPayorPlans?: { facilityPayorPlanId: number; cfgPayorPlanId: number; facilityPayorPlanName: string }[];
}

interface LevelsOfCareProps {
    levels:  FacilityLevelOfCare[] | null;
    /** From Redux; used when VOB has no rateAsPayorPlan */
    rateAsPayorPlan?: {
        payor: Payor;
        plan: Plan | null;
    } | null;
    /** From current VOB (API or vob.payor + vob.plan); when present, used to traverse options and display */
    vobRateAsPayorPlan?: {
        payor: Payor;
        plan: Plan | null;
    } | null;
    facilityPayersState?: FacilityPayerOption[] | null;
    payersMap?: Map<number, { payorName?: string; payorPlans?: { payorPlanId: number; planName?: string }[] }>;
    /** When set, dropdown shows this instead of rateAsPayorPlan (e.g. user selected for display only, Redux unchanged) */
    selectedFacilityPlanId?: number;
    /** Form selected facility id; when !== vobFacilityId, use Redux rateAsPayorPlan so dropdown has a valid value */
    selectedFacility?: number | '';
    /** VOB's facility id */
    vobFacilityId?: number;
    /** Called when user selects a different option; parent typically syncs IN/OUT tab and calls loadAndSetRateAsPayorPlan to update Redux */
    onRateAsPayorPlanChange?: (facilityPlanId: number) => void;
    networkType: NetworkType;
    memberType: MemberType;
    setSavedLOCDataMap: (updater: (prevMap: Map<number, any>) => Map<number, any>) => void;
    savedLOCDataMap?: Map<number, Map<string, any>>;
    savedFormDataMap?: Map<string, any>;
    levelErrors?: Map<number, {
        deductibleTotalError?: boolean;
        deductibleRemainingError?: boolean;
        oopmTotalError?: boolean;
        oopmRemainingError?: boolean;
        copayError?: boolean;
        maxDaysError?: boolean;
        coinsuranceError?: boolean;
        deductibleTotalErrorMessage?: string;
        deductibleRemainingErrorMessage?: string;
        oopmTotalErrorMessage?: string;
        oopmRemainingErrorMessage?: string;
        copayErrorMessage?: string;
        maxDaysErrorMessage?: string;
        coinsuranceErrorMessage?: string;
    }>;
}

export const LevelsOfCare: React.FC<LevelsOfCareProps> = ({
                                                              levels,
                                                              rateAsPayorPlan,
                                                              vobRateAsPayorPlan,
                                                              facilityPayersState,
                                                              payersMap,
                                                              selectedFacilityPlanId,
                                                              selectedFacility,
                                                              vobFacilityId,
                                                              onRateAsPayorPlanChange,
                                                              networkType,
                                                              memberType,
                                                              setSavedLOCDataMap,
                                                              savedLOCDataMap = new Map(),
                                                              savedFormDataMap = new Map(),
                                                              levelErrors = new Map()
                                                          }) => {
    const [expandedAccordion, setExpandedAccordion] = useState<number | false>(false);

    // State to track button pressed states for each level of care
    const [disabledFields, setDisabledFields] = useState<Record<number, Record<string, boolean>>>({});
    const [overriddenLevels, setOverriddenLevels] = useState<Record<number, boolean>>({});
    const [overriddenServiceLevels, setOverriddenServiceLevels] = useState<Record<number, boolean>>({});
    const [disabledMaxDaysFields, setDisabledMaxDaysFields] = useState<Record<number, boolean>>({});

    const [inputValues, setInputValues] = useState<Record<number, Record<string, string | boolean>>>({});

    const [originalValues, setOriginalValues] = useState<Record<string, Record<string, any>>>({});

    // Get VOB classifications from Redux
    const vobClassifications = useSelector(
        (state: RootState) => state.vobContext.vobClassifications
    );




    useEffect(() => {
        if (!levels) return;

        const newOverriddenServiceLevels: Record<number, boolean> = {};
        const newOverriddenLevels: Record<number, boolean> = {};
        const newDisabledFields: Record<number, Record<string, boolean>> = {};
        const newDisabledMaxDaysFields: Record<number, boolean> = {};
        let hasAnyValues = false;

        levels?.forEach(level => {
            const vobClassificationId = level.vobClassificationId;
            if (!vobClassificationId) return;

            newDisabledFields[vobClassificationId] = {
                deductible: true,
                oopm: true,
                copay: true,
                coinsurance: true
            };
            newDisabledMaxDaysFields[vobClassificationId] = true;

            if (savedLOCDataMap && savedLOCDataMap.size > 0) {
                const levelData = savedLOCDataMap.get(vobClassificationId);
                if (levelData) {
                    const combinationKey = `${networkType}_${memberType}`;
                    const savedData = levelData.get(combinationKey);

                    if (savedData) {
                        const hasValues = Object.values(savedData).some(value =>
                            value !== undefined && value !== null && value !== '' && value !== false
                        );

                        if (hasValues) {
                            hasAnyValues = true;
                            if (level.vobClassificationId) {
                                levels?.forEach(l => {
                                    if (l.vobClassificationId === level.vobClassificationId) {
                                        const lId = l.vobClassificationId;
                                        if (lId) {
                                            newOverriddenServiceLevels[lId] = true;
                                            const lData = savedLOCDataMap.get(lId);
                                            const lCombinationKey = `${networkType}_${memberType}`;
                                            const lSavedData = lData?.get(lCombinationKey) || {};

                                            newDisabledFields[lId] = {
                                                deductible: !(lSavedData.total || lSavedData.remaining),
                                                oopm: !(lSavedData.oopmTotal || lSavedData.oopmRemaining),
                                                copay: !(lSavedData.perDayVisit || lSavedData.maxDays),
                                                coinsurance: !lSavedData.patient
                                            };
                                            newDisabledMaxDaysFields[lId] = !lSavedData.maxDays;
                                        }
                                    }
                                });
                            } else {
                                newOverriddenLevels[vobClassificationId] = true;
                                newDisabledFields[vobClassificationId] = {
                                    deductible: !(savedData.total || savedData.remaining),
                                    oopm: !(savedData.oopmTotal || savedData.oopmRemaining),
                                    copay: !(savedData.perDayVisit || savedData.maxDays),
                                    coinsurance: !savedData.patient
                                };
                                newDisabledMaxDaysFields[vobClassificationId] = !savedData.maxDays;
                            }
                        }
                    }
                }
            }
        });

        setOverriddenServiceLevels(prev => ({
            ...prev,
            ...newOverriddenServiceLevels
        }));
        setOverriddenLevels(prev => ({
            ...prev,
            ...newOverriddenLevels
        }));
        setDisabledFields(prev => {
            const merged = { ...prev };
            Object.keys(newDisabledFields).forEach(vobClassificationIdStr => {
                const vobClassificationId = parseInt(vobClassificationIdStr);
                const existingFields = merged[vobClassificationId];
                const newFields = newDisabledFields[vobClassificationId];
                if (existingFields) {
                    const updatedFields = { ...existingFields };
                    Object.keys(newFields).forEach(fieldKey => {
                        if (newFields[fieldKey] === false || existingFields[fieldKey] === undefined) {
                            updatedFields[fieldKey] = newFields[fieldKey];
                        }
                    });
                    merged[vobClassificationId] = updatedFields;
                } else {
                    merged[vobClassificationId] = newFields;
                }
            });
            return merged;
        });
        setDisabledMaxDaysFields(prev => {
            const merged = { ...prev };
            Object.keys(newDisabledMaxDaysFields).forEach(vobClassificationIdStr => {
                const vobClassificationId = parseInt(vobClassificationIdStr);
                const existingValue = merged[vobClassificationId];
                const newValue = newDisabledMaxDaysFields[vobClassificationId];
                if (newValue === false || existingValue === undefined) {
                    merged[vobClassificationId] = newValue;
                }
            });
            return merged;
        });
    }, [savedLOCDataMap, levels, networkType, memberType]);

    const handleAccordionChange = (vobClassificationId: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedAccordion(isExpanded ? vobClassificationId : false);
    };

    // Helper function to format number with 2 decimal places if not a whole number
    const formatNumberValue = (value: string): string => {
        if (!value) return value;

        const num = parseFloat(value);
        if (isNaN(num)) return value;

        // Check if the number is a whole number
        return Number.isInteger(num) ? num.toString() : num.toFixed(2);
    };

    // Handle button click to toggle disabled state for a field group
    const handleButtonClick = (vobClassificationId: number, fieldGroup: string) => {
        const isCurrentlyDisabled = isFieldGroupDisabled(vobClassificationId, fieldGroup);

        if (!isCurrentlyDisabled) {
            const combinationKey = `${networkType}_${memberType}`;
            const levelData = savedLOCDataMap.get(vobClassificationId);
            const currentData = levelData?.get(combinationKey) || {};
            let originalValues: Record<string, any> = {};
            if (fieldGroup === 'deductible') {
                originalValues = {
                    total: currentData.total || '',
                    remaining: currentData.remaining || ''
                };
            } else if (fieldGroup === 'oopm') {
                originalValues = {
                    oopmTotal: currentData.oopmTotal || '',
                    oopmRemaining: currentData.oopmRemaining || '',
                    deductibleIncluded: currentData.deductibleIncluded || false
                };
            } else if (fieldGroup === 'copay') {
                originalValues = {
                    perDayVisit: currentData.perDayVisit || '',
                    maxDays: currentData.maxDays || ''
                };
            } else if (fieldGroup === 'coinsurance') {
                originalValues = {
                    patient: currentData.patient || ''
                };
            }

            setSavedLOCDataMap(prevMap => {
                const newMap = new Map(prevMap);
                const levelData = newMap.get(vobClassificationId) || new Map();
                const updatedData = { ...currentData };

                if (fieldGroup === 'deductible') {
                    updatedData.total = '';
                    updatedData.remaining = '';
                } else if (fieldGroup === 'oopm') {
                    updatedData.oopmTotal = '';
                    updatedData.oopmRemaining = '';
                    updatedData.deductibleIncluded = false;
                } else if (fieldGroup === 'copay') {
                    updatedData.perDayVisit = '';
                    updatedData.maxDays = '';
                } else if (fieldGroup === 'coinsurance') {
                    updatedData.patient = '';
                }

                levelData.set(combinationKey, updatedData);
                newMap.set(vobClassificationId, levelData);
                return newMap;
            });

            setOriginalValues(prev => ({
                ...prev,
                [`${vobClassificationId}_${fieldGroup}`]: originalValues
            }));
        } else {
            const originalValuesForGroup = originalValues[`${vobClassificationId}_${fieldGroup}`];
            if (originalValuesForGroup) {
                setSavedLOCDataMap(prevMap => {
                    const newMap = new Map(prevMap);
                    const levelData = newMap.get(vobClassificationId) || new Map();
                    const combinationKey = `${networkType}_${memberType}`;
                    const currentData = levelData.get(combinationKey) || {};

                    const updatedData = {
                        ...currentData,
                        ...originalValuesForGroup
                    };

                    levelData.set(combinationKey, updatedData);
                    newMap.set(vobClassificationId, levelData);
                    return newMap;
                });
            }
        }

        setDisabledFields(prev => {
            const levelFields = prev[vobClassificationId] || {};
            const updatedLevelFields = {
                deductible: levelFields.deductible !== undefined ? levelFields.deductible : true,
                oopm: levelFields.oopm !== undefined ? levelFields.oopm : true,
                copay: levelFields.copay !== undefined ? levelFields.copay : true,
                coinsurance: levelFields.coinsurance !== undefined ? levelFields.coinsurance : true,
                [fieldGroup]: !isCurrentlyDisabled
            };
            return {
                ...prev,
                [vobClassificationId]: updatedLevelFields
            };
        });

        if (fieldGroup === 'copay') {
            setDisabledMaxDaysFields(prev => ({
                ...prev,
                [vobClassificationId]: !isCurrentlyDisabled
            }));
        }
    };

    const handleMaxDaysButtonClick = (vobClassificationId: number) => {
        const isCurrentlyDisabled = disabledMaxDaysFields[vobClassificationId] || false;

        if (!isCurrentlyDisabled) {
            const combinationKey = `${networkType}_${memberType}`;
            const levelData = savedLOCDataMap.get(vobClassificationId);
            const currentData = levelData?.get(combinationKey) || {};
            const originalValue = currentData.maxDays || '';
            setSavedLOCDataMap(prevMap => {
                const newMap = new Map(prevMap);
                const levelData = newMap.get(vobClassificationId) || new Map();
                const updatedData = { ...currentData };
                updatedData.maxDays = '';

                levelData.set(combinationKey, updatedData);
                newMap.set(vobClassificationId, levelData);
                return newMap;
            });

            setOriginalValues(prev => ({
                ...prev,
                [`${vobClassificationId}_maxDays`]: { maxDays: originalValue }
            }));
        } else {
            const originalValuesForMaxDays = originalValues[`${vobClassificationId}_maxDays`];
            if (originalValuesForMaxDays) {
                setSavedLOCDataMap(prevMap => {
                    const newMap = new Map(prevMap);
                    const levelData = newMap.get(vobClassificationId) || new Map();
                    const combinationKey = `${networkType}_${memberType}`;
                    const currentData = levelData.get(combinationKey) || {};

                    const updatedData = {
                        ...currentData,
                        ...originalValuesForMaxDays
                    };

                    levelData.set(combinationKey, updatedData);
                    newMap.set(vobClassificationId, levelData);
                    return newMap;
                });
            }
        }

        setDisabledMaxDaysFields(prev => ({
            ...prev,
            [vobClassificationId]: !isCurrentlyDisabled
        }));
    };



    // Handle override button click to toggle override state for a level of care
    const handleOverrideClick = (vobClassificationId: number) => {
        const isCurrentlyOverridden = overriddenLevels[vobClassificationId] || false;

        // If turning on this button, turn off the other button
        if (!isCurrentlyOverridden) {
            setOverriddenServiceLevels(prev => ({
                ...prev,
                [vobClassificationId]: false
            }));

            setDisabledFields(prev => {
                const levelFields = prev[vobClassificationId] || {};
                const updatedLevelFields = {
                    ...levelFields,
                    deductible: false,
                    oopm: false,
                    copay: false,
                    coinsurance: false
                };
                return {
                    ...prev,
                    [vobClassificationId]: updatedLevelFields
                };
            });

            setDisabledMaxDaysFields(prev => ({
                ...prev,
                [vobClassificationId]: false
            }));
        }

        // Toggle this button
        setOverriddenLevels(prev => ({
            ...prev,
            [vobClassificationId]: !isCurrentlyOverridden
        }));
    };

    // Handle override service level button click
    const handleOverrideServiceLevelClick = (vobClassificationId: number) => {
        // Find the level with this ID
        const level = levels?.find(l => l.vobClassificationId === vobClassificationId);
        if (!level) return;

        // Use the new function to toggle service level override for all levels with the same classification ID
        toggleServiceLevelOverrideForClassification(level);

        const isCurrentlyOverridden = isServiceLevelOverridden(vobClassificationId);

        if (!isCurrentlyOverridden) {
            setDisabledFields(prev => {
                const levelFields = prev[vobClassificationId] || {};
                const updatedLevelFields = {
                    deductible: true,
                    oopm: true,
                    copay: levelFields.copay !== undefined ? levelFields.copay : true,
                    coinsurance: levelFields.coinsurance !== undefined ? levelFields.coinsurance : true,
                };
                return {
                    ...prev,
                    [vobClassificationId]: updatedLevelFields
                };
            });

            const combinationKey = `${networkType}_${memberType}`;
            const formData = savedFormDataMap.get(combinationKey);

            if (formData) {
                setSavedLOCDataMap(prevMap => {
                    const newMap = new Map(prevMap);
                    const levelData = newMap.get(vobClassificationId) || new Map();

                    const locData = {
                        perDayVisit: formData.copay || '',
                        maxDays: formData.maxDays || '',
                        patient: formData.coinsurance || '',
                    };

                    levelData.set(combinationKey, locData);
                    newMap.set(vobClassificationId, levelData);
                    return newMap;
                });
            }
        } else {
            setSavedLOCDataMap(prevMap => {
                const newMap = new Map(prevMap);
                newMap.delete(vobClassificationId);
                return newMap;
            });
        }
    };

    // Check if a field group is disabled for a specific level
    const isFieldGroupDisabled = (vobClassificationId: number, fieldGroup: string): boolean => {
        const fieldState = disabledFields[vobClassificationId]?.[fieldGroup];
        const result = fieldState !== undefined ? fieldState : true;

        return result;
    };

    // Check if a level of care is overridden
    const isLevelOverridden = (vobClassificationId: number): boolean => {
        return overriddenLevels[vobClassificationId] || false;
    };

    // Check if a service level is overridden
    const isServiceLevelOverridden = (vobClassificationId: number): boolean => {
        return overriddenServiceLevels[vobClassificationId] || false;
    };

    const isFieldDisabled = (vobClassificationId: number, fieldGroup: string): boolean => {
        const isLevelOverride = isLevelOverridden(vobClassificationId);
        const isServiceLevelOverride = isServiceLevelOverridden(vobClassificationId);
        const isFieldGroupDisabledState = isFieldGroupDisabled(vobClassificationId, fieldGroup);

        if (!isLevelOverride && !isServiceLevelOverride) {
            return true;
        }

        return isFieldGroupDisabledState;
    };

    const isMaxDaysFieldDisabled = (vobClassificationId: number): boolean => {
        const isLevelOverride = isLevelOverridden(vobClassificationId);
        const isServiceLevelOverride = isServiceLevelOverridden(vobClassificationId);

        if (!isLevelOverride && !isServiceLevelOverride) {
            return true;
        }

        return disabledMaxDaysFields[vobClassificationId] || false;
    };



    // Function to get classification name by ID
    const getClassificationName = (vobClassificationId?: number) => {
        if (!vobClassificationId) {
            return '';
        }

        if (!vobClassifications || vobClassifications.length === 0) {
            return 'Loading classification...';
        }

        const classification = vobClassifications.find(
            (c) => c.vobClassificationId === vobClassificationId
        );

        return classification ? classification.vobClassificationName : '';
    };

    const hasServiceLevelOverrideForClassification = (
        classificationId: number | undefined
    ): boolean => {
        if (!classificationId) return false;

        // Find all levels with this classification ID (falls back to empty array if levels is undefined)
        const levelsWithSameClassification =
            levels?.filter(level => level.vobClassificationId === classificationId) ?? [];

        // Check if any of these levels has service level override
        return levelsWithSameClassification.some(
            level => overriddenServiceLevels[level.vobClassificationId] || false
        );
    };


    // Get benefits text based on override states
    const getBenefitsText = (level: FacilityLevelOfCare & { sortOrder?: number }): string => {
        const vobClassificationId = level?.vobClassificationId;
        const isLevelOverride = isLevelOverridden(vobClassificationId);
        const isServiceLevelOverride = isServiceLevelOverridden(vobClassificationId);
        const hasClassificationServiceOverride = hasServiceLevelOverrideForClassification(level.vobClassificationId);

        if (isServiceLevelOverride) {
            return "Using custom service level values";
        } else if (hasClassificationServiceOverride && isLevelOverride) {
            return "Using LOC custom values";
        } else if (hasClassificationServiceOverride) {
            return "Using service level default values";
        } else if (isLevelOverride) {
            return "Using custom values";
        } else {
            return "Benefits are the same as defaults";
        }
    };

    // Toggle service level override for all levels with the same classification ID
    const toggleServiceLevelOverrideForClassification = (level: FacilityLevelOfCare & { sortOrder?: number }) => {
        if (!level.vobClassificationId) return;

        // Find all levels with the same classification ID
        const levelsWithSameClassification = levels?.filter(l =>
            l.vobClassificationId === level.vobClassificationId
        );

        // Get the current state of the first level (to determine if we're turning on or off)
        const vobClassificationId = level.vobClassificationId;
        const isCurrentlyOverridden = isServiceLevelOverridden(vobClassificationId);

        // Create a new overriddenServiceLevels object
        const newOverriddenServiceLevels = { ...overriddenServiceLevels };

        // Update all levels with the same classification ID
        levelsWithSameClassification?.forEach(l => {
            const id = l.vobClassificationId;
            if (!isCurrentlyOverridden) {
                newOverriddenServiceLevels[id] = true;
                // If we're enabling service level override, disable level override
                setOverriddenLevels(prev => {
                    const updated = { ...prev };
                    delete updated[id];
                    return updated;
                });

                setDisabledFields(prev => {
                    const levelFields = prev[id] || {};
                    const updatedLevelFields = {
                        ...levelFields,
                        deductible: false,
                        oopm: false,
                        copay: false,
                        coinsurance: false
                    };

                    return {
                        ...prev,
                        [id]: updatedLevelFields
                    };
                });

                setDisabledMaxDaysFields(prev => ({
                    ...prev,
                    [id]: false
                }));
            } else {
                delete newOverriddenServiceLevels[id];
            }
        });

        // Update the state
        setOverriddenServiceLevels(newOverriddenServiceLevels);

        if (!isCurrentlyOverridden) {
            setSavedLOCDataMap(prevMap => {
                const newMap = new Map(prevMap);
                levelsWithSameClassification?.forEach(l => {
                    const id = l.vobClassificationId;
                    if (id) {
                        newMap.delete(id);
                    }
                });
                return newMap;
            });
        }
    };


    const handleInputChange = (vobClassificationId: number, fieldName: string, value: string | boolean) => {
        const integerOnlyFields = ['perDayVisit', 'maxDays', 'patient'];
        if (integerOnlyFields.includes(fieldName) && typeof value === 'string') {
            if (value !== '' && !/^-?\d+$/.test(value)) {
                return;
            }
        }

        setInputValues(prev => ({
            ...prev,
            [vobClassificationId]: {
                ...prev[vobClassificationId],
                [fieldName]: value
            }
        }));

        setSavedLOCDataMap(prevMap => {
            const newMap = new Map(prevMap);
            const levelData = newMap.get(vobClassificationId) || new Map();
            const combinationKey = `${networkType}_${memberType}`;

            const currentData = levelData.get(combinationKey) || {};
            const updatedData = {
                ...currentData,
                [fieldName]: value
            };

            levelData.set(combinationKey, updatedData);
            newMap.set(vobClassificationId, levelData);
            return newMap;
        });
    };

    const getFieldValue = (vobClassificationId: number, fieldName: string): string | boolean => {
        const levelData = savedLOCDataMap.get(vobClassificationId);
        if (levelData) {
            const combinationKey = `${networkType}_${memberType}`;
            const savedData = levelData.get(combinationKey);
            if (savedData && savedData[fieldName] !== undefined) {
                return savedData[fieldName];
            }
        }
        return  '';
    };

    const getFieldError = (vobClassificationId: number, fieldName: string): boolean => {
        const levelError = levelErrors.get(vobClassificationId);
        if (!levelError) return false;

        switch (fieldName) {
            case 'total':
                return levelError.deductibleTotalError || false;
            case 'remaining':
                return levelError.deductibleRemainingError || false;
            case 'oopmTotal':
                return levelError.oopmTotalError || false;
            case 'oopmRemaining':
                return levelError.oopmRemainingError || false;
            case 'perDayVisit':
                return levelError.copayError || false;
            case 'maxDays':
                return levelError.maxDaysError || false;
            case 'patient':
                return levelError.coinsuranceError || false;
            default:
                return false;
        }
    };

    // Helper function to get error message for a field
    const getFieldErrorMessage = (vobClassificationId: number, fieldName: string): string => {
        const levelError = levelErrors.get(vobClassificationId);
        if (!levelError) return '';

        switch (fieldName) {
            case 'total':
                return levelError.deductibleTotalErrorMessage || '';
            case 'remaining':
                return levelError.deductibleRemainingErrorMessage || '';
            case 'oopmTotal':
                return levelError.oopmTotalErrorMessage || '';
            case 'oopmRemaining':
                return levelError.oopmRemainingErrorMessage || '';
            case 'perDayVisit':
                return levelError.copayErrorMessage || '';
            case 'maxDays':
                return levelError.maxDaysErrorMessage || '';
            case 'patient':
                return levelError.coinsuranceErrorMessage || '';
            default:
                return '';
        }
    };

    const filteredLevels = levels?.filter(level => level.isCovered === true) || [];

    // When user changed facility (form facility !== VOB facility), use Redux rateAsPayorPlan so dropdown value exists in new facility's options.
    const facilityChanged = selectedFacility != null && selectedFacility !== '' && vobFacilityId != null && Number(selectedFacility) !== Number(vobFacilityId);

    // Prefer "same as before change" when it exists in the new facility: match vobRateAsPayorPlan by cfg payor/plan id in facilityPayersState.
    const preferredMatchBeforeChange = (() => {
        if (!facilityChanged || !vobRateAsPayorPlan?.payor?.payorId || !vobRateAsPayorPlan?.plan?.payorPlanId || !facilityPayersState?.length) return null;
        const vobPayorId = Number(vobRateAsPayorPlan.payor.payorId);
        const vobPlanId = Number(vobRateAsPayorPlan.plan.payorPlanId);
        for (const payer of facilityPayersState) {
            if (Number(payer.cfgPayorId) !== vobPayorId) continue;
            const plan = (payer.facilityPayorPlans ?? []).find((p: { cfgPayorPlanId?: number }) => Number(p.cfgPayorPlanId) === vobPlanId);
            if (plan) return { payer, plan };
        }
        return null;
    })();

    const displayRateAs = facilityChanged
        ? (preferredMatchBeforeChange
            ? {
                payor: {
                    payorId: preferredMatchBeforeChange.payer.cfgPayorId,
                    payorName: payersMap?.get(preferredMatchBeforeChange.payer.cfgPayorId)?.payorName,
                    facilityPayorName: preferredMatchBeforeChange.payer.facilityPayorName,
                    inNetwork: preferredMatchBeforeChange.payer.inNetwork
                } as Payor,
                plan: {
                    payorPlanId: preferredMatchBeforeChange.plan.cfgPayorPlanId,
                    facilityPlanId: preferredMatchBeforeChange.plan.facilityPayorPlanId,
                    planName: payersMap?.get(preferredMatchBeforeChange.payer.cfgPayorId)?.payorPlans?.find((p: { payorPlanId: number }) => Number(p.payorPlanId) === preferredMatchBeforeChange.plan.cfgPayorPlanId)?.planName,
                    facilityPlanName: preferredMatchBeforeChange.plan.facilityPayorPlanName
                } as Plan
            }
            : rateAsPayorPlan)
        : (vobRateAsPayorPlan ?? rateAsPayorPlan);

    // When VOB has rateAsPayorPlan: find matching option in facilityPayersState and use its id; otherwise use default (selectedFacilityPlanId or legacy).
    // For historical VOBs with no rateAsPayorPlan, displayRateAs is vob.payor/vob.plan (only payorPlanId); resolve facilityPayorPlanId from facilityPayersState.
    const rateAsSelectValue = (() => {
        if (selectedFacilityPlanId != null) return selectedFacilityPlanId;
        if (facilityChanged && preferredMatchBeforeChange) return preferredMatchBeforeChange.plan.facilityPayorPlanId;
        const source = displayRateAs;
        if (!source?.plan || !facilityPayersState?.length) {
            return source?.plan?.facilityPlanId ?? (source?.plan as { facilityPayorPlanId?: number })?.facilityPayorPlanId ?? '';
        }
        let targetId = source.plan.facilityPlanId ?? (source.plan as { facilityPayorPlanId?: number }).facilityPayorPlanId;
        if (targetId == null && source.payor?.payorId != null && source.plan?.payorPlanId != null) {
            const payorId = Number(source.payor.payorId);
            const planId = Number(source.plan.payorPlanId);
            for (const payer of facilityPayersState) {
                if (Number(payer.cfgPayorId) !== payorId) continue;
                const plan = (payer.facilityPayorPlans ?? []).find((p: { cfgPayorPlanId?: number }) => Number(p.cfgPayorPlanId) === planId);
                if (plan?.facilityPayorPlanId != null) {
                    targetId = plan.facilityPayorPlanId;
                    break;
                }
            }
        }
        if (targetId == null) return '';
        const numTarget = Number(targetId);
        for (const payer of facilityPayersState) {
            for (const plan of payer.facilityPayorPlans ?? []) {
                if (Number(plan.facilityPayorPlanId) === numTarget) return plan.facilityPayorPlanId;
            }
        }
        return numTarget;
    })();

    return (
        <Box sx={{ mt: 4 }} test-id="vob-levels-of-care-container">
            <Typography variant="h6" sx={{ mb: 2 }} test-id="vob-levels-of-care-title">
                Covered Levels of Care
                {displayRateAs && (facilityPayersState?.length ? (
                    <FormControl size="small" sx={{ minWidth: 280, ml: 1 }} test-id="vob-rate-as-payor-plan-select">
                        <InputLabel id="vob-rate-as-label">Rate as</InputLabel>
                        <Select
                            labelId="vob-rate-as-label"
                            id="vob-rate-as-payor-plan-select"
                            value={rateAsSelectValue}
                            onChange={(e) => onRateAsPayorPlanChange?.(Number(e.target.value))}
                            label="Rate as"
                            sx={{ fontWeight: 'normal', color: 'text.secondary' }}
                        >
                            {facilityPayersState
                                .filter((payer) => {
                                    const name = (payer.facilityPayorName || payersMap?.get(payer.cfgPayorId)?.payorName || '').toLowerCase();
                                    return !name.includes('self-pay') && !name.includes('self pay');
                                })
                                .flatMap((payer) =>
                                    (payer.facilityPayorPlans || []).map((plan) => {
                                        const payorName = payer.facilityPayorName || payersMap?.get(payer.cfgPayorId)?.payorName || '';
                                        const planName = plan.facilityPayorPlanName || payersMap?.get(payer.cfgPayorId)?.payorPlans?.find((p) => Number(p.payorPlanId) === Number(plan.cfgPayorPlanId))?.planName || '';
                                        const label = `${payorName}${planName ? `/${planName}` : ''} - ${payer.inNetwork ? 'In Network' : 'Out of Network'}`;
                                        return (
                                            <MenuItem key={plan.facilityPayorPlanId} value={plan.facilityPayorPlanId} test-id={`vob-rate-as-option-${plan.facilityPayorPlanId}`}>
                                                {label}
                                            </MenuItem>
                                        );
                                    })
                                )}
                        </Select>
                    </FormControl>
                ) : (
                    <Typography component="span" variant="body2" color="textSecondary" sx={{ fontWeight: 'normal', ml: 1 }} test-id="vob-levels-of-care-rate-as">
                        - Rate as: ({displayRateAs?.payor?.facilityPayorName || displayRateAs?.payor?.payorName}/{displayRateAs?.plan?.planName}) - {displayRateAs?.payor?.inNetwork ? 'In Network' : 'Out of Network'}
                    </Typography>
                ))}
            </Typography>
            {filteredLevels && filteredLevels.length > 0 ? (
                filteredLevels.map((level) => {
                    const levelId = level?.levelOfCareId!.toString();
                    const vobClassificationId = level?.vobClassificationId;
                    const isOverridden = isLevelOverridden(vobClassificationId);

                    return (
                        <Accordion
                            key={level.levelOfCareId}
                            test-id={`vob-level-accordion-${levelId}`}
                            expanded={expandedAccordion === vobClassificationId}
                            onChange={handleAccordionChange(vobClassificationId)}
                            sx={{ mb: 2 }}
                        >
                            <AccordionSummary
                                test-id={`vob-level-summary-${levelId}`}
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`level-${level.levelOfCareId}-content`}
                                id={`level-${level.levelOfCareId}-header`}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }} test-id={`vob-level-summary-content-${levelId}`}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }} test-id={`vob-level-name-${levelId}`}>
                                            {level.facilityLevelOfCareCode} {level.facilityLevelOfCareName}
                                        </Typography>
                                        <DefaultBenefitsText test-id={`vob-level-benefits-${levelId}`}>
                                            {getBenefitsText(level)}
                                        </DefaultBenefitsText>
                                    </Box>
                                    {level.vobClassificationId && (
                                        <ClassificationNameText test-id={`vob-level-classification-${levelId}`}>
                                            Service Level: {getClassificationName(level.vobClassificationId)}
                                        </ClassificationNameText>
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails test-id={`vob-level-details-${levelId}`}>
                                <Grid container spacing={2} test-id={`vob-level-details-grid-${levelId}`}>
                                    {/* Row 1: Deductible and OOPM - Always Hidden */}
                                    {/* 
                                    <Grid item xs={12} container spacing={2} alignItems="stretch" test-id={`vob-level-row-1-${levelId}`}>
                                        <Grid item xs={4}>
                                            <FramedSection test-id={`vob-level-deductible-section-${levelId}`}>
                                                <FrameLabel hasButton>Deductible</FrameLabel>
                                                <FrameLabelButton 
                                                    test-id={`vob-level-deductible-disable-button-${levelId}`}
                                                    onClick={() => handleButtonClick(vobClassificationId, 'deductible')}
                                                    title={isFieldGroupDisabled(vobClassificationId, 'deductible') ? "Enable deductible fields" : "Disable deductible fields"}
                                                >
                                                    <NoSymbol isPressed={isFieldGroupDisabled(vobClassificationId, 'deductible')} />
                                                </FrameLabelButton>
                                                <Grid container spacing={2} alignItems="center" test-id={`vob-level-deductible-fields-${levelId}`}>
                                                    <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <VOBNumericField
                                                            test-id={`vob-level-deductible-total-${levelId}`}
                                                            fullWidth
                                                            label="Total $"
                                                            size="small"
                                                            disabled={isFieldDisabled(vobClassificationId, 'deductible')}
                                                            onChange={(e) => handleInputChange(vobClassificationId, 'total', e.target.value)}
                                                            value={getFieldValue(vobClassificationId, 'total')}
                                                            error={getFieldError(vobClassificationId, 'total')}
                                                            helperText={getFieldError(vobClassificationId, 'total') ? getFieldErrorMessage(vobClassificationId, 'total') : ''}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <VOBNumericField
                                                            test-id={`vob-level-deductible-remaining-${levelId}`}
                                                            fullWidth
                                                            label="Remaining $"
                                                            size="small"
                                                            disabled={isFieldDisabled(vobClassificationId, 'deductible')}
                                                            onChange={(e) => handleInputChange(vobClassificationId, 'remaining', e.target.value)}
                                                            value={getFieldValue(vobClassificationId, 'remaining')}
                                                            error={getFieldError(vobClassificationId, 'remaining')}
                                                            helperText={getFieldError(vobClassificationId, 'remaining') ? getFieldErrorMessage(vobClassificationId, 'remaining') : ''}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </FramedSection>
                                        </Grid>
                                        
                                        <Grid item xs={8}>
                                            <FramedSection test-id={`vob-level-oopm-section-${levelId}`}>
                                                <FrameLabel hasButton>Out of Pocket Maximum</FrameLabel>
                                                <FrameLabelButton 
                                                    test-id={`vob-level-oopm-disable-button-${levelId}`}
                                                    onClick={() => handleButtonClick(vobClassificationId, 'oopm')}
                                                    title={isFieldGroupDisabled(vobClassificationId, 'oopm') ? "Enable OOPM fields" : "Disable OOPM fields"}
                                                >
                                                    <NoSymbol isPressed={isFieldGroupDisabled(vobClassificationId, 'oopm')} />
                                                </FrameLabelButton>
                                                <Grid container spacing={2} alignItems="center" test-id={`vob-level-oopm-fields-${levelId}`}>
                                                    <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <VOBNumericField
                                                            test-id={`vob-level-oopm-total-${levelId}`}
                                                            fullWidth
                                                            label="Total $"
                                                            size="small"
                                                            disabled={isFieldDisabled(vobClassificationId, 'oopm')}
                                                            onChange={(e) => handleInputChange(vobClassificationId, 'oopmTotal', e.target.value)}
                                                            value={getFieldValue(vobClassificationId, 'oopmTotal')}
                                                            error={getFieldError(vobClassificationId, 'oopmTotal')}
                                                            helperText={getFieldError(vobClassificationId, 'oopmTotal') ? getFieldErrorMessage(vobClassificationId, 'oopmTotal') : ''}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <VOBNumericField
                                                            test-id={`vob-level-oopm-remaining-${levelId}`}
                                                            fullWidth
                                                            label="Remaining $"
                                                            size="small"
                                                            disabled={isFieldDisabled(vobClassificationId, 'oopm')}
                                                            onChange={(e) => handleInputChange(vobClassificationId, 'oopmRemaining', e.target.value)}
                                                            value={getFieldValue(vobClassificationId, 'oopmRemaining')}
                                                            error={getFieldError(vobClassificationId, 'oopmRemaining')}
                                                            helperText={getFieldError(vobClassificationId, 'oopmRemaining') ? getFieldErrorMessage(vobClassificationId, 'oopmRemaining') : ''}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <NoWrapFormControlLabel
                                                            test-id={`vob-level-deductible-included-checkbox-${levelId}`}
                                                            control={
                                                                <Checkbox
                                                                    test-id={`vob-level-deductible-included-checkbox-input-${levelId}`}
                                                                    disabled={isFieldDisabled(vobClassificationId, 'oopm')}
                                                                    onChange={(e) => handleInputChange(vobClassificationId, 'deductibleIncluded', e.target.checked)}
                                                                    checked={getFieldValue(vobClassificationId, 'deductibleIncluded') === true}
                                                                />
                                                            }
                                                            label="Deductible Included"
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </FramedSection>
                                        </Grid>
                                    </Grid>
                                    */}

                                    {/* Row 2: Co-pay and Coinsurance */}
                                    <Grid item xs={12} container spacing={2} alignItems="center" test-id={`vob-level-row-2-${levelId}`}>
                                        {/* Co-pay Section */}
                                        <Grid item xs={4}>
                                            <FramedSection test-id={`vob-level-copay-section-${levelId}`}>
                                                <FrameLabel hasButton>Co-pay</FrameLabel>
                                                <FrameLabelButton
                                                    test-id={`vob-level-copay-disable-button-${levelId}`}
                                                    onClick={() => handleButtonClick(vobClassificationId, 'copay')}
                                                    title={isFieldGroupDisabled(vobClassificationId, 'copay') ? "Enable co-pay fields" : "Disable co-pay fields"}
                                                >
                                                    <NoSymbol isPressed={isFieldGroupDisabled(vobClassificationId, 'copay')} />
                                                </FrameLabelButton>
                                                <Grid container spacing={2} alignItems="center" test-id={`vob-level-copay-fields-${levelId}`}>
                                                    <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <VOBNumericField
                                                            test-id={`vob-level-copay-input-${levelId}`}
                                                            fullWidth
                                                            label="Per Day/Visit $"
                                                            size="small"
                                                            disabled={isFieldDisabled(vobClassificationId, 'copay')}
                                                            onChange={(e) => handleInputChange(vobClassificationId, 'perDayVisit', e.target.value)}
                                                            value={getFieldValue(vobClassificationId, 'perDayVisit')}
                                                            error={getFieldError(vobClassificationId, 'perDayVisit')}
                                                            helperText={getFieldError(vobClassificationId, 'perDayVisit') ? getFieldErrorMessage(vobClassificationId, 'perDayVisit') : ''}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <LabelButtonWrapper>
                                                            <FieldLabelButton
                                                                test-id={`vob-level-max-days-disable-button-${levelId}`}
                                                                onClick={() => handleMaxDaysButtonClick(vobClassificationId)}
                                                                title={disabledMaxDaysFields[vobClassificationId] ? "Enable Max Days field" : "Disable Max Days field"}
                                                                size="small"
                                                            >
                                                                <NoSymbol isPressed={disabledMaxDaysFields[vobClassificationId] || false} />
                                                            </FieldLabelButton>
                                                            <VOBNumericField
                                                                test-id={`vob-level-max-days-input-${levelId}`}
                                                                fullWidth
                                                                label="Max Days"
                                                                size="small"
                                                                disabled={isMaxDaysFieldDisabled(vobClassificationId)}
                                                                onChange={(e) => handleInputChange(vobClassificationId, 'maxDays', e.target.value)}
                                                                value={getFieldValue(vobClassificationId, 'maxDays')}
                                                                error={getFieldError(vobClassificationId, 'maxDays')}
                                                                helperText={getFieldError(vobClassificationId, 'maxDays') ? getFieldErrorMessage(vobClassificationId, 'maxDays') : ''}
                                                                sx={{
                                                                    '& .MuiInputLabel-root': {
                                                                        marginLeft: '16px'
                                                                    }
                                                                }}
                                                            />
                                                        </LabelButtonWrapper>
                                                    </Grid>
                                                </Grid>
                                            </FramedSection>
                                        </Grid>

                                        {/* Coinsurance Section */}
                                        <Grid item xs={4}>
                                            <FramedSection test-id={`vob-level-coinsurance-section-${levelId}`}>
                                                <FrameLabel hasButton>Coinsurance</FrameLabel>
                                                <FrameLabelButton
                                                    test-id={`vob-level-coinsurance-disable-button-${levelId}`}
                                                    onClick={() => handleButtonClick(vobClassificationId, 'coinsurance')}
                                                    title={isFieldGroupDisabled(vobClassificationId, 'coinsurance') ? "Enable coinsurance field" : "Disable coinsurance field"}
                                                >
                                                    <NoSymbol isPressed={isFieldGroupDisabled(vobClassificationId, 'coinsurance')} />
                                                </FrameLabelButton>
                                                <Grid container spacing={2} alignItems="center" test-id={`vob-level-coinsurance-fields-${levelId}`}>
                                                    <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <VOBNumericField
                                                            test-id={`vob-level-coinsurance-input-${levelId}`}
                                                            fullWidth
                                                            label="Patient %"
                                                            size="small"
                                                            disabled={isFieldDisabled(vobClassificationId, 'coinsurance')}
                                                            onChange={(e) => handleInputChange(vobClassificationId, 'patient', e.target.value)}
                                                            value={getFieldValue(vobClassificationId, 'patient')}
                                                            error={getFieldError(vobClassificationId, 'patient')}
                                                            helperText={getFieldError(vobClassificationId, 'patient') ? getFieldErrorMessage(vobClassificationId, 'patient') : ''}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}></Grid>
                                                </Grid>
                                            </FramedSection>
                                        </Grid>
                                        <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} test-id={`vob-level-override-buttons-${levelId}`}>
                                            {/*<OverrideButton*/}
                                            {/*    test-id={`vob-level-override-button-${levelId}`}*/}
                                            {/*    variant={isLevelOverridden(vobClassificationId) ? "contained" : "outlined"}*/}
                                            {/*    color={isLevelOverridden(vobClassificationId) ? "primary" : "inherit"}*/}
                                            {/*    onClick={() => handleOverrideClick(vobClassificationId)}*/}
                                            {/*    startIcon={<OverrideSymbol isPressed={isLevelOverridden(vobClassificationId)} />}*/}
                                            {/*    sx={{ mb: 1, width: '100%' }}*/}
                                            {/*>*/}
                                            {/*    {isLevelOverridden(vobClassificationId) ? "Using Custom Values" : "Override LOC Values"}*/}
                                            {/*</OverrideButton>*/}
                                            <OverrideButton
                                                test-id={`vob-level-service-override-button-${levelId}`}
                                                variant={isServiceLevelOverridden(vobClassificationId) ? "contained" : "outlined"}
                                                color={isServiceLevelOverridden(vobClassificationId) ? "primary" : "inherit"}
                                                onClick={() => handleOverrideServiceLevelClick(vobClassificationId)}
                                                sx={{ width: '100%' }}
                                            >
                                                {isServiceLevelOverridden(vobClassificationId) ? "Using Custom Service Level" : "Override Service Level"}
                                            </OverrideButton>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    );
                })
            ) : (
                <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }} test-id="vob-levels-of-care-empty-message">
                    There's no level of care covered under this payor
                </Typography>
            )}
        </Box>
    );
}; 